/**
 * Returns a random element from an array
 * 
 * @param array The source array to get a random element from
 * @returns A random element from the array or undefined if array is empty
 * @throws Error if the input is not an array
 * 
 * @example
 * const colors = ['red', 'green', 'blue'];
 * const randomColor = randomFromArray(colors); // Could be any of the three colors
 */
export function randomFromArray<T>(array: T[]): T {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Shuffles an array using the Fisher-Yates algorithm
 * 
 * @param array The array to shuffle
 * @returns A new shuffled array
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [1, 2, 3, 4, 5];
 * const shuffled = shuffle(numbers); // [3, 1, 5, 2, 4] (random order)
 */
export function shuffle<T>(array: T[]): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  const result = [...array];
  
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [result[i], result[j]] = [result[j], result[i]];
  }
  
  return result;
}

/**
 * Splits an array into chunks of specified size
 * 
 * @param array The array to split into chunks
 * @param size The size of each chunk
 * @returns An array of arrays, each of specified size (except possibly the last one)
 * @throws Error if the input is not an array or size is not a positive number
 * 
 * @example
 * const numbers = [1, 2, 3, 4, 5, 6, 7];
 * const chunks = chunk(numbers, 3); // [[1, 2, 3], [4, 5, 6], [7]]
 */
export function chunk<T>(array: T[], size: number): T[][] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  if (!Number.isInteger(size) || size <= 0) {
    throw new Error('Chunk size must be a positive integer');
  }
  
  const result: T[][] = [];
  
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  
  return result;
}

/**
 * Returns unique values from an array
 * 
 * @param array The array to get unique values from
 * @returns A new array with duplicate values removed
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [1, 2, 2, 3, 4, 4, 5];
 * const uniqueNumbers = unique(numbers); // [1, 2, 3, 4, 5]
 */
export function unique<T>(array: T[]): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return [...new Set(array)];
}

/**
 * Finds duplicate values in an array
 * 
 * @param array The array to find duplicates in
 * @returns A new array containing only the duplicate values (without repetition)
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [1, 2, 2, 3, 4, 4, 5];
 * const duplicates = findDuplicates(numbers); // [2, 4]
 */
export function findDuplicates<T>(array: T[]): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  const seen = new Set<T>();
  const duplicates = new Set<T>();
  
  for (const item of array) {
    if (seen.has(item)) {
      duplicates.add(item);
    } else {
      seen.add(item);
    }
  }
  
  return [...duplicates];
}

/**
 * Flattens a nested array structure
 * 
 * @param array The array to flatten
 * @param depth The maximum recursion depth (default is Infinity for full flattening)
 * @returns A new flattened array
 * @throws Error if the input is not an array
 * 
 * @example
 * const nestedArray = [1, [2, [3, 4], 5]];
 * const flattened = flatten(nestedArray); // [1, 2, 3, 4, 5]
 */
export function flatten<T>(array: (T | T[])[], depth = Infinity): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array.flat(depth) as T[];
}

/**
 * Finds the intersection of two arrays
 * 
 * @param arrayA First array
 * @param arrayB Second array
 * @returns A new array with elements that exist in both input arrays
 * @throws Error if either input is not an array
 * 
 * @example
 * const array1 = [1, 2, 3, 4];
 * const array2 = [3, 4, 5, 6];
 * const common = intersection(array1, array2); // [3, 4]
 */
export function intersection<T>(arrayA: T[], arrayB: T[]): T[] {
  if (!Array.isArray(arrayA) || !Array.isArray(arrayB)) {
    throw new Error('Inputs must be arrays');
  }
  
  const setB = new Set(arrayB);

  return arrayA.filter(item => setB.has(item));
}

/**
 * Finds the difference between two arrays (elements in A that are not in B)
 * 
 * @param arrayA First array
 * @param arrayB Second array
 * @returns A new array with elements from arrayA that don't exist in arrayB
 * @throws Error if either input is not an array
 * 
 * @example
 * const array1 = [1, 2, 3, 4];
 * const array2 = [3, 4, 5, 6];
 * const diff = difference(array1, array2); // [1, 2]
 */
export function difference<T>(arrayA: T[], arrayB: T[]): T[] {
  if (!Array.isArray(arrayA) || !Array.isArray(arrayB)) {
    throw new Error('Inputs must be arrays');
  }
  
  const setB = new Set(arrayB);

  return arrayA.filter(item => !setB.has(item));
}

/**
 * Groups array elements by a key extracted using the provided function
 * 
 * @param array The array to group
 * @param keyFn Function that returns the grouping key for an element
 * @returns An object with keys as group names and values as arrays of items
 * @throws Error if the input is not an array
 * 
 * @example
 * const people = [
 *   { name: 'Alice', age: 25 },
 *   { name: 'Bob', age: 30 },
 *   { name: 'Charlie', age: 25 }
 * ];
 * const byAge = groupBy(people, person => person.age);
 * // { '25': [{name: 'Alice', age: 25}, {name: 'Charlie', age: 25}], '30': [{name: 'Bob', age: 30}] }
 */
export function groupBy<T, K extends string | number | symbol>(
  array: T[],
  keyFn: (item: T) => K
): Record<K, T[]> {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array.reduce((result, item) => {
    const key = keyFn(item);

    result[key] = result[key] || [];
    result[key].push(item);

    return result;
  }, {} as Record<K, T[]>);
}

/**
 * Paginates an array into pages
 * 
 * @param array The array to paginate
 * @param pageSize The number of items per page
 * @param pageNumber The page number to retrieve (0-based)
 * @returns An object containing the paginated items and pagination metadata
 * @throws Error if inputs are invalid
 * 
 * @example
 * const items = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 * const page = paginate(items, 3, 1);
 * // { items: [4, 5, 6], pageCount: 4, totalItems: 10, currentPage: 1 }
 */
export function paginate<T>(
  array: T[],
  pageSize: number,
  pageNumber: number
): { items: T[]; pageCount: number; totalItems: number; currentPage: number } {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw new Error('Page size must be a positive integer');
  }
  
  if (!Number.isInteger(pageNumber) || pageNumber < 0) {
    throw new Error('Page number must be a non-negative integer');
  }
  
  const totalItems = array.length;
  const pageCount = Math.ceil(totalItems / pageSize);
  const startIndex = pageNumber * pageSize;
  const items = array.slice(startIndex, startIndex + pageSize);
  
  return {
    items,
    pageCount,
    totalItems,
    currentPage: pageNumber
  };
}

/**
 * Sorts an array by a property or a derived value
 * 
 * @param array The array to sort
 * @param keyFn Function that returns the value to sort by
 * @param direction Sort direction: 'asc' (default) or 'desc'
 * @returns A new sorted array
 * @throws Error if the input is not an array
 * 
 * @example
 * const people = [
 *   { name: 'Alice', age: 25 },
 *   { name: 'Bob', age: 30 },
 *   { name: 'Charlie', age: 20 }
 * ];
 * const byAge = sortBy(people, p => p.age); // Sorted by age ascending
 * const byAgeDesc = sortBy(people, p => p.age, 'desc'); // Sorted by age descending
 */
export function sortBy<T, K>(
  array: T[],
  keyFn: (item: T) => K,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  const multiplier = direction === 'desc' ? -1 : 1;
  
  return [...array].sort((a, b) => {
    const keyA = keyFn(a);
    const keyB = keyFn(b);
    
    if (keyA < keyB) {return -1 * multiplier;}

    if (keyA > keyB) {return 1 * multiplier;}

    return 0;
  });
}

/**
 * Finds array elements that match a predicate function
 * 
 * @param array The array to search
 * @param predicate Function that returns true for matching elements
 * @returns A new array with matching elements
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [1, 2, 3, 4, 5];
 * const evens = findBy(numbers, n => n % 2 === 0); // [2, 4]
 */
export function findBy<T>(array: T[], predicate: (item: T) => boolean): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array.filter(predicate);
}

/**
 * Checks if all elements in the array satisfy a predicate function
 * 
 * @param array The array to check
 * @param predicate Function that should return true for every element
 * @returns True if all elements satisfy the predicate, false otherwise
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [2, 4, 6, 8];
 * const allEven = every(numbers, n => n % 2 === 0); // true
 */
export function every<T>(array: T[], predicate: (item: T) => boolean): boolean {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array.every(predicate);
}

/**
 * Checks if any element in the array satisfies a predicate function
 * 
 * @param array The array to check
 * @param predicate Function that should return true for at least one element
 * @returns True if any element satisfies the predicate, false otherwise
 * @throws Error if the input is not an array
 * 
 * @example
 * const numbers = [1, 3, 5, 8];
 * const hasEven = some(numbers, n => n % 2 === 0); // true
 */
export function some<T>(array: T[], predicate: (item: T) => boolean): boolean {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  return array.some(predicate);
}

/**
 * Creates an array with a range of numbers
 * 
 * @param start The start value (inclusive)
 * @param end The end value (inclusive)
 * @param step The increment step (default is 1)
 * @returns An array containing the range of numbers
 * @throws Error if inputs are invalid
 * 
 * @example
 * const range1 = range(1, 5); // [1, 2, 3, 4, 5]
 * const range2 = range(0, 10, 2); // [0, 2, 4, 6, 8, 10]
 */
export function range(start: number, end: number, step = 1): number[] {
  if (!Number.isInteger(start) || !Number.isInteger(end) || !Number.isInteger(step)) {
    throw new Error('All parameters must be integers');
  }
  
  if (step === 0) {
    throw new Error('Step cannot be zero');
  }
  
  if ((end < start && step > 0) || (end > start && step < 0)) {
    throw new Error('Invalid range parameters');
  }
  
  const length = Math.floor((end - start) / step) + 1;

  return Array.from({ length }, (_, i) => start + i * step);
}

/**
 * Computes the average value of an array of numbers
 * 
 * @param array The array of numbers
 * @returns The average value or 0 for empty arrays
 * @throws Error if the input is not an array of numbers
 * 
 * @example
 * const numbers = [1, 2, 3, 4, 5];
 * const avg = average(numbers); // 3
 */
export function average(array: number[]): number {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array');
  }
  
  if (array.length === 0) {
    return 0;
  }
  
  if (!array.every(item => typeof item === 'number')) {
    throw new Error('All elements must be numbers');
  }
  
  return array.reduce((sum, value) => sum + value, 0) / array.length;
}
